// ABOUTME: Main provider factory - creates backend providers based on configuration
// ABOUTME: Central point for switching between Cloudflare, mock, or other providers

import type { BackendProvider, ProviderConfig } from './interfaces'
import { createMockProvider } from './mock'
import { createCloudflareProvider, type CloudflareConfig } from './cloudflare'

/**
 * Global provider instance
 */
let currentProvider: BackendProvider | null = null

/**
 * Initialize the backend provider
 *
 * @param config Provider configuration
 * @returns Configured backend provider
 */
export function initializeProvider(config: ProviderConfig): BackendProvider {
  switch (config.type) {
    case 'mock':
      currentProvider = createMockProvider()
      break

    case 'cloudflare':
      currentProvider = createCloudflareProvider(config.config as unknown as CloudflareConfig)
      break

    default:
      throw new Error(`Unknown provider type: ${config.type}`)
  }

  return currentProvider
}

/**
 * Get the current backend provider
 *
 * Throws an error if provider has not been initialized
 */
export function getProvider(): BackendProvider {
  if (!currentProvider) {
    throw new Error('Provider not initialized. Call initializeProvider() first.')
  }
  return currentProvider
}

/**
 * Get provider from environment variables
 *
 * Automatically selects provider based on VITE_PROVIDER env var
 * Falls back to mock provider if Cloudflare credentials are not set
 */
export function getProviderFromEnv(): BackendProvider {
  const providerType = import.meta.env.VITE_PROVIDER || 'mock'

  if (providerType === 'cloudflare') {
    const apiUrl = import.meta.env.VITE_API_URL || ''

    return initializeProvider({
      type: 'cloudflare',
      config: { apiUrl },
    })
  }

  return initializeProvider({ type: 'mock', config: {} })
}

// Re-export types for convenience
export type {
  BackendProvider,
  ProviderConfig,
  IAuthProvider,
  IDatabaseProvider,
  IStorageProvider,
  User,
  Session,
  AuthResult,
  QueryFilter,
  QueryOptions,
  DatabaseResult,
  RealtimeCallback,
  UploadOptions,
  UploadResult,
  FileMetadata,
} from './interfaces'
